import { Check, Download, Loader2 } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';
import { Button } from '@/components/ui/button';
import { useAuthor } from '@/hooks/useAuthor';
import { useOfflinePublications } from '@/hooks/useOfflinePublications';
import { useToast } from '@/hooks/useToast';
import { OfflineQuotaError } from '@/lib/offlineLibrary';
import { getPublicationRefs, publicationTitle } from '@/lib/nkbip01';
import { cn } from '@/lib/utils';

interface OfflinePublicationSaveButtonProps {
  /** The publication index event (kind 30040). */
  index: NostrEvent;
  /** The resolved section events, in the index's display order. */
  sections: NostrEvent[] | undefined;
  /** Whether sections are still being fetched — saving is blocked until they resolve. */
  sectionsLoading?: boolean;
  size?: 'sm' | 'default';
  className?: string;
}

/**
 * Saves a whole NKBIP-01 publication — index plus every section — into
 * the browser's offline library (see `useOfflinePublications`).
 *
 * Saving is deliberately blocked until the sections have resolved.
 * Unlike a wiki article, the index event contains no prose of its own,
 * so persisting it before its sections arrive would store a table of
 * contents whose every entry is missing offline — worse than not
 * offering the save at all.
 *
 * If some sections resolved but others didn't (a relay didn't have
 * them, or they've since vanished), the save still proceeds with what's
 * available and the toast says how many were captured — a partial book
 * is genuinely useful, but the user should know it's partial.
 */
export function OfflinePublicationSaveButton({
  index,
  sections,
  sectionsLoading,
  size = 'sm',
  className,
}: OfflinePublicationSaveButtonProps) {
  const { isSaved, save, remove } = useOfflinePublications();
  const { toast } = useToast();
  const author = useAuthor(index.pubkey);

  const saved = isSaved(index);
  const expected = getPublicationRefs(index).length;
  const resolved = sections?.length ?? 0;

  const handleClick = () => {
    if (saved) {
      remove(index);
      toast({
        title: 'Removed from offline library',
        description: `"${publicationTitle(index)}" is no longer saved on this device.`,
      });
      return;
    }

    try {
      save(index, sections ?? [], {
        name: author.data?.metadata?.name,
        picture: author.data?.metadata?.picture,
      });

      const partial = expected > 0 && resolved < expected;
      toast({
        title: 'Saved for offline reading',
        description: partial
          ? `"${publicationTitle(index)}" saved with ${resolved} of ${expected} sections — the rest couldn't be fetched.`
          : `"${publicationTitle(index)}" and ${resolved} ${resolved === 1 ? 'section' : 'sections'} are now available without a connection.`,
      });
    } catch (error) {
      toast({
        title: "Couldn't save this publication",
        description:
          error instanceof OfflineQuotaError
            ? 'This browser is out of storage space. Publications are large — remove a few saved items and try again.'
            : 'Something went wrong writing to this browser\u2019s storage.',
        variant: 'destructive',
      });
    }
  };

  const waiting = !saved && Boolean(sectionsLoading);

  return (
    <Button
      type="button"
      variant={saved ? 'default' : 'outline'}
      size={size}
      onClick={handleClick}
      disabled={waiting}
      title={
        saved
          ? 'Remove from offline library'
          : waiting
            ? 'Waiting for all sections to load…'
            : 'Save this publication and all its sections for offline reading'
      }
      className={cn('gap-1.5', className)}
    >
      {waiting ? (
        <Loader2 className="size-4 animate-spin" />
      ) : saved ? (
        <Check className="size-4" />
      ) : (
        <Download className="size-4" />
      )}
      <span className="hidden sm:inline">{saved ? 'Saved' : 'Save'}</span>
    </Button>
  );
}
